import React, { useEffect } from 'react';
import { Info, Users, Sparkles, Target, Award, Lightbulb } from 'lucide-react';

function AcmPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const starryBackground = {
    backgroundImage: `
      radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.8), transparent),
      radial-gradient(2px 2px at 60px 70px, rgba(255,255,255,0.6), transparent),
      radial-gradient(1px 1px at 50px 50px, rgba(255,255,255,0.4), transparent),
      radial-gradient(1px 1px at 130px 80px, rgba(255,255,255,0.5), transparent),
      radial-gradient(2px 2px at 90px 10px, rgba(255,255,255,0.7), transparent),
      radial-gradient(1.5px 1.5px at 180px 120px, rgba(255,255,255,0.6), transparent),
      radial-gradient(1px 1px at 200px 180px, rgba(255,255,255,0.4), transparent),
      radial-gradient(2px 2px at 250px 50px, rgba(255,255,255,0.8), transparent)
    `,
    backgroundSize: '200px 200px, 300px 300px, 400px 400px, 250px 250px, 350px 350px, 280px 280px, 320px 320px, 380px 380px',
    animation: 'starAnimation 60s linear infinite'
  };

  const sections = [
    {
      icon: Info,
      title: 'ACM Nedir?',
      description: 'ACM (Association for Computing Machinery), bilgisayar mühendisliği bilimi ve uygulamalarını bir araya getiren 1947\'de kurulan uluslararası bir organizasyondur. ACM, dünyanın en büyük bilimsel ve eğitimsel bilgisayar topluluğudur.',
      color: 'blue',
      showLogo: true
    },
    {
      icon: Users,
      title: 'ACM Iğdır Öğrenci Topluluğu',
      description: 'ACM Iğdır Öğrenci Topluluğu, teknoloji ve bilgisayar bilimleriyle ilgilenen öğrencilere yönelik çeşitli etkinlikler ve projeler sunan bir topluluktur. Topluluğumuz, öğrencilerin hem sosyal hem de teknik becerilerini geliştirmeyi hedefler.',
      color: 'purple'
    },
    {
      icon: Target,
      title: 'Neden ACM Iğdır\'a Katılmalıyım?',
      description: 'Teknolojiye meraklıysanız, bilgisayar bilimleri alanında kendinizi geliştirmek istiyorsanız ve aynı zamanda harika bir sosyal çevre oluşturmak istiyorsanız, ACM Iğdır tam size göre! Etkinliklerimizde ve projelerimizde yer alarak, hem kariyerinize katkıda bulunacak hem de yeni arkadaşlıklar kuracaksınız.',
      color: 'green'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
      purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
      green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900 pt-28 pb-16 px-5 relative overflow-hidden"
      style={starryBackground}
    >
      {/* Dekoratif elementler */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/10 to-transparent pointer-events-none" />
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-500/20 rounded-2xl backdrop-blur-sm border border-blue-500/30">
              <Award className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
              ACM Hakkında
            </h1>
          </div>
          <p className="text-blue-200/60 text-lg max-w-2xl mx-auto">
            Dünyanın en büyük bilgisayar bilimleri topluluğunu tanıyın
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div
                key={index}
                className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 md:p-10 transition-all duration-500 hover:border-white/20"
                style={{ animation: `slideInUp 0.5s ease-out ${index * 0.1}s backwards` }}
              >
                <div className="flex flex-col md:flex-row items-start gap-6">
                  {/* Icon */}
                  <div className={`p-4 rounded-2xl bg-gradient-to-br ${getColorClasses(section.color)} border flex-shrink-0`}>
                    <IconComponent className="w-8 h-8" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors">
                      {section.title}
                    </h2>
                    <p className="text-blue-100/70 text-lg leading-relaxed">
                      {section.description}
                    </p>
                  </div>

                  {/* Logo for ACM Nedir */}
                  {section.showLogo && (
                    <div className="hidden md:flex items-center justify-center flex-shrink-0">
                      <img 
                        src={require('../styles/acm.png')} 
                        alt="ACM Logo" 
                        className="w-24 h-24 opacity-80 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full border border-white/10">
            <Lightbulb className="w-5 h-5 text-yellow-400" />
            <span className="text-blue-200/80">Teknolojinin geleceğini birlikte şekillendirelim!</span>
            <Sparkles className="w-5 h-5 text-blue-400" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcmPage;
